import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  MdCalendarToday,
  MdPerson,
  MdCheckCircle,
  MdRadioButtonUnchecked,
  MdClose,
  MdFreeBreakfast,
  MdHistory,
  MdSearch,
} from "react-icons/md";
import {
  getBookings,
  createBooking,
  updateBookingStatus,
} from "../../services/booking.service";
import { getEmployee } from "../../services/employee.service";
import { getServices } from "../../services/inventory.service";
import { toast } from "react-toastify";
// import { t } from "i18next";
import { LogoLoading } from "../../components/common/utlity/utility";

const toYmd = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toMinutes = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return null;

  const ampm = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let hour = Number(ampm[1]);
    const minute = Number(ampm[2]);
    const period = ampm[3].toUpperCase();
    if (period === "AM" && hour === 12) hour = 0;
    if (period === "PM" && hour !== 12) hour += 12;
    return hour * 60 + minute;
  }

  const hhmm = value.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) {
    const hour = Number(hhmm[1]);
    const minute = Number(hhmm[2]);
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return hour * 60 + minute;
    }
  }

  return null;
};

const toHHMM = (minutes) => {
  if (typeof minutes !== "number" || Number.isNaN(minutes) || minutes < 0)
    return "";
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const parseDurationMinutes = (durationValue, fallback = 60) => {
  if (
    durationValue === undefined ||
    durationValue === null ||
    durationValue === ""
  ) {
    return fallback;
  }

  const raw = String(durationValue).trim().toLowerCase();
  const numeric = Number(raw);
  if (!Number.isNaN(numeric) && numeric > 0) {
    return numeric;
  }

  const match = raw.match(/(\d+(?:\.\d+)?)/);
  if (!match) return fallback;

  const value = Number(match[1]);
  if (Number.isNaN(value) || value <= 0) return fallback;

  if (raw.includes("hour") || raw.includes("hr")) {
    return Math.round(value * 60);
  }

  return Math.round(value);
};

const normalizePriceType = (priceType, hourlyRate = 0) => {
  const raw = String(priceType || "")
    .trim()
    .toLowerCase();
  if (raw.includes("hour")) return "hourly";

  const parsedRate = Number(hourlyRate || 0);
  if (!Number.isNaN(parsedRate) && parsedRate > 0) {
    return "hourly";
  }

  return "fixed";
};

const DASHBOARD_CACHE_TTL_MS = 5 * 60 * 1000;

const getDashboardCacheKey = (businessUuid) =>
  `booking-dashboard:${businessUuid}`;

const readDashboardCache = (businessUuid) => {
  if (typeof window === "undefined" || !businessUuid) return null;

  try {
    const cached = window.sessionStorage.getItem(
      getDashboardCacheKey(businessUuid),
    );
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    if (!parsed || !parsed.expiresAt || parsed.expiresAt <= Date.now()) {
      window.sessionStorage.removeItem(getDashboardCacheKey(businessUuid));
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn("Failed to read booking dashboard cache", error);
    return null;
  }
};

const writeDashboardCache = (businessUuid, data) => {
  if (typeof window === "undefined" || !businessUuid) return;

  try {
    window.sessionStorage.setItem(
      getDashboardCacheKey(businessUuid),
      JSON.stringify({
        ...data,
        expiresAt: Date.now() + DASHBOARD_CACHE_TTL_MS,
      }),
    );
  } catch (error) {
    console.warn("Failed to write booking dashboard cache", error);
  }
};

const normalizeBookings = (bookingData) =>
  Array.isArray(bookingData) ? bookingData : [];

const normalizeEmployees = (employeeResp) => {
  const employeeRows = employeeResp?.data?.data || employeeResp?.data || [];
  return Array.isArray(employeeRows) ? employeeRows : [];
};

const normalizeServices = (servicesResp) => {
  const servicesData = servicesResp?.data?.data || servicesResp?.data || [];
  return Array.isArray(servicesData) ? servicesData : [];
};

// Generate continuous time slots for an employee
const generateEmployeeTimeSlots = (
  employee,
  bookings,
  servicesMap,
  businessStart,
  businessEnd,
  breakStart,
  breakEnd,
) => {
  const slots = [];

  // Get all bookings for this employee
  const employeeBookings = bookings
    .filter((b) => b.employeeId === employee.id)
    .sort((a, b) => {
      const aTime = toMinutes(a.time);
      const bTime = toMinutes(b.time);
      return (aTime || 0) - (bTime || 0);
    });

  // If no bookings, return the entire business day as one slot
  if (employeeBookings.length === 0) {
    const slotEnd = Math.min(businessEnd, breakStart || businessEnd);
    if (businessStart < slotEnd) {
      slots.push({
        start: businessStart,
        end: slotEnd,
        type: "free",
        bookings: [],
      });
    }
    // Add break if exists
    if (breakStart && breakEnd && breakStart < breakEnd) {
      slots.push({
        start: breakStart,
        end: breakEnd,
        type: "break",
        bookings: [],
      });
    }
    // Add afternoon free slot
    if (breakEnd && breakEnd < businessEnd) {
      slots.push({
        start: breakEnd,
        end: businessEnd,
        type: "free",
        bookings: [],
      });
    }
    return slots;
  }

  let currentTime = businessStart;

  // Process each booking and create slots
  employeeBookings.forEach((booking, index) => {
    const bookingStart = toMinutes(booking.time);
    if (bookingStart === null) return;

    const service = servicesMap.get(booking.serviceId);
    const duration = parseDurationMinutes(service?.serviceDuration, 60);
    const bookingEnd = bookingStart + duration;

    // Check if there's a break in between
    if (
      breakStart &&
      breakEnd &&
      breakStart > currentTime &&
      breakStart < bookingStart
    ) {
      // Add free slot before break
      if (currentTime < breakStart) {
        slots.push({
          start: currentTime,
          end: breakStart,
          type: "free",
          bookings: [],
        });
      }
      // Add break slot
      slots.push({
        start: breakStart,
        end: breakEnd,
        type: "break",
        bookings: [],
      });
      currentTime = breakEnd;
    }

    // Add free slot before booking
    if (currentTime < bookingStart) {
      slots.push({
        start: currentTime,
        end: bookingStart,
        type: "free",
        bookings: [],
      });
    }

    // Add booking slot
    slots.push({
      start: bookingStart,
      end: bookingEnd,
      type: "booked",
      bookings: [booking],
      service: service,
    });

    currentTime = bookingEnd;
  });

  // Handle remaining time after last booking
  if (currentTime < businessEnd) {
    // Check if break is after last booking
    if (breakStart && breakEnd && breakStart > currentTime) {
      slots.push({
        start: currentTime,
        end: breakStart,
        type: "free",
        bookings: [],
      });
      slots.push({
        start: breakStart,
        end: breakEnd,
        type: "break",
        bookings: [],
      });
      currentTime = breakEnd;
    }

    if (currentTime < businessEnd) {
      slots.push({
        start: currentTime,
        end: businessEnd,
        type: "free",
        bookings: [],
      });
    }
  }

  return slots;
};

export default function OnlineServiceBookingDashboardPage() {
  const { bussinessUuid } = useParams();

  const [bookings, setBookings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(toYmd(new Date()));
  const [searchQuery, setSearchQuery] = useState("");
  const [manualAssignOpen, setManualAssignOpen] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [manualAssignableServices, setManualAssignableServices] = useState([]);
  const [manualAssignForm, setManualAssignForm] = useState({
    serviceId: "",
    name: "",
    mobile: "",
    email: "",
    paymentMode: "cash",
  });

  useEffect(() => {
    const loadData = async () => {
      if (!bussinessUuid) return;

      const cachedData = readDashboardCache(bussinessUuid);
      const cachedBookings = cachedData?.bookingsByDate?.[selectedDate];
      const cachedEmployees = cachedData?.employees || [];
      const cachedServices = cachedData?.services || [];

      if (cachedBookings && cachedEmployees.length && cachedServices.length) {
        setBookings(normalizeBookings(cachedBookings));
        setEmployees(cachedEmployees);
        setServices(cachedServices);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const shouldFetchEmployees = !cachedEmployees.length;
        const shouldFetchServices = !cachedServices.length;

        const [bookingData, employeeResp, servicesResp] = await Promise.all([
          getBookings(bussinessUuid, { date: selectedDate }),
          shouldFetchEmployees
            ? getEmployee(bussinessUuid, { limit: 300 })
            : Promise.resolve({ data: { data: cachedEmployees } }),
          shouldFetchServices
            ? getServices(bussinessUuid, { all: "true" })
            : Promise.resolve({ data: { data: cachedServices } }),
        ]);

        const normalizedBookings = normalizeBookings(bookingData);
        const normalizedEmployees = shouldFetchEmployees
          ? normalizeEmployees(employeeResp)
          : cachedEmployees;
        const normalizedServices = shouldFetchServices
          ? normalizeServices(servicesResp)
          : cachedServices;

        const nextCache = {
          employees: normalizedEmployees,
          services: normalizedServices,
          bookingsByDate: {
            ...(cachedData?.bookingsByDate || {}),
            [selectedDate]: normalizedBookings,
          },
        };

        writeDashboardCache(bussinessUuid, nextCache);
        setBookings(normalizedBookings);
        setEmployees(normalizedEmployees);
        setServices(normalizedServices);
      } catch (error) {
        console.error("Failed to load booking dashboard data", error);
        setBookings([]);
        setEmployees([]);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [bussinessUuid, selectedDate]);

  const servicesById = useMemo(() => {
    const map = new Map();
    services.forEach((service) => {
      if (service?.serviceId) {
        map.set(service.serviceId, service);
      }
    });
    return map;
  }, [services]);

  // Get filtered bookings for selected date
  const filteredBookings = useMemo(
    () =>
      bookings.filter((booking) => {
        if (booking.date !== selectedDate) return false;
        return true;
      }),
    [bookings, selectedDate],
  );

  const activeBookings = useMemo(
    () =>
      filteredBookings.filter(
        (booking) =>
          String(booking?.status || "").toLowerCase() !== "cancelled",
      ),
    [filteredBookings],
  );

  // Process employees with their time slots
  const employeeSlotsData = useMemo(() => {
    const result = [];

    // Get business hours from first service or default
    const firstService = services[0];
    const businessStart = toMinutes(firstService?.openTime || "10:00") ?? 600;
    const businessEnd = toMinutes(firstService?.closeTime || "18:00") ?? 1080;
    const breakStart =
      toMinutes(firstService?.breakStartTime || "13:00") ?? 780;
    const breakEnd = toMinutes(firstService?.breakEndTime || "14:00") ?? 840;

    // Get unique employees from both employees list and bookings
    const employeeMap = new Map();

    // Add employees from employee list
    employees.forEach((emp) => {
      const id = emp.employeeID || emp.employeeId || emp._id;
      if (id) {
        employeeMap.set(id, {
          id: id,
          name: emp.employeeName || emp.name || emp.fullName || "Employee",
          mongoId: emp._id || "",
        });
      }
    });

    // Add employees from bookings
    activeBookings.forEach((booking) => {
      if (booking.employeeId && !employeeMap.has(booking.employeeId)) {
        employeeMap.set(booking.employeeId, {
          id: booking.employeeId,
          name: booking.employeeName || "Employee",
          mongoId: "",
        });
      }
    });

    // Generate slots for each employee
    employeeMap.forEach((employee) => {
      const employeeBookings = activeBookings.filter(
        (b) => b.employeeId === employee.id,
      );
      const slots = generateEmployeeTimeSlots(
        employee,
        employeeBookings,
        servicesById,
        businessStart,
        businessEnd,
        breakStart,
        breakEnd,
      );

      result.push({
        employee,
        slots,
        bookings: employeeBookings,
      });
    });

    return result;
  }, [employees, activeBookings, servicesById, services]);

  const filteredEmployeeSlotsData = useMemo(() => {
    if (!searchQuery) return employeeSlotsData;
    const lowerQuery = searchQuery.toLowerCase();
    return employeeSlotsData.filter((data) =>
      data.employee.name.toLowerCase().includes(lowerQuery),
    );
  }, [employeeSlotsData, searchQuery]);

  // Get current time in minutes
  const getCurrentTimeMinutes = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };

  // Get current date in YMD format
  const getCurrentDateYmd = () => {
    return toYmd(new Date());
  };

  // Check if a slot is in the past (slot end time is before current time with 15 min grace period)
  const isPastSlot = (slotStart, slotEnd) => {
    if (!selectedDate) return false;

    const todayYmd = getCurrentDateYmd();
    if (selectedDate !== todayYmd) return false;

    const nowMinutes = getCurrentTimeMinutes();
    // A slot is considered past if the end time is more than 15 minutes ago
    // This gives a 15 minute grace period for current/future slots
    return slotEnd < nowMinutes - 15;
  };

  // Check if current time is within this slot or within 15 minutes of starting
  const isCurrentlyInSlot = (slotStart, slotEnd) => {
    if (!selectedDate) return false;

    const todayYmd = getCurrentDateYmd();
    if (selectedDate !== todayYmd) return false;

    const nowMinutes = getCurrentTimeMinutes();
    // Slot is current if:
    // 1. Current time is within the slot, OR
    // 2. Slot starts within the next 15 minutes (grace period for upcoming slots)
    const isWithinSlot = nowMinutes >= slotStart && nowMinutes < slotEnd;
    const isStartingSoon =
      slotStart - nowMinutes >= 0 && slotStart - nowMinutes <= 15;
    const isRecentlyStarted =
      nowMinutes - slotStart >= 0 && nowMinutes - slotStart <= 15;

    return isWithinSlot || isStartingSoon || isRecentlyStarted;
  };

  // Check if a specific time is in the future (for booking validation)
  const isTimeInFuture = (timeStr) => {
    if (!selectedDate) return false;

    const todayYmd = getCurrentDateYmd();
    if (selectedDate !== todayYmd) return true; // Future date, so it's in the future

    const timeMinutes = toMinutes(timeStr);
    if (timeMinutes === null) return false;

    const nowMinutes = getCurrentTimeMinutes();
    // Allow bookings up to 15 minutes in the past (grace period)
    return timeMinutes >= nowMinutes - 15;
  };

  // Get slot status with clear past/future indication
  const getSlotStatus = (slot) => {
    if (slot.type === "break") return "break";
    if (slot.type === "booked") return "booked";

    const isPast = isPastSlot(slot.start, slot.end);
    const isCurrent = isCurrentlyInSlot(slot.start, slot.end);

    if (isPast) return "past";
    if (isCurrent) return "current";
    return "future";
  };

  // Split a free slot into past and future portions based on current time
  const splitSlotByCurrentTime = (slot) => {
    if (!selectedDate) return [slot];

    const todayYmd = getCurrentDateYmd();
    if (selectedDate !== todayYmd) return [slot];

    if (slot.type !== "free") return [slot];

    const nowMinutes = getCurrentTimeMinutes();
    const gracePeriod = 15; // 15 minutes grace period

    // If slot is entirely in the future (start > current time + grace period)
    if (slot.start > nowMinutes + gracePeriod) {
      return [slot];
    }

    // If slot is entirely in the past (end < current time - grace period)
    if (slot.end < nowMinutes - gracePeriod) {
      return [{ ...slot, type: "past" }];
    }

    // Slot is within grace period - show as current/free
    // Adjust start time to current time if it's within grace period
    const adjustedStart = slot.start < nowMinutes ? nowMinutes : slot.start;

    if (adjustedStart < slot.end) {
      return [
        {
          start: adjustedStart,
          end: slot.end,
          type: "free",
          bookings: [],
        },
      ];
    }

    return [slot];
  };

  const openManualAssignModal = (employee, slot) => {
    const status = getSlotStatus(slot);

    // Only allow assignment for future or current slots
    if (status === "past") {
      toast.error("Cannot assign: This time slot has already passed");
      return;
    }

    if (slot.type === "break") {
      toast.error("Cannot assign: This is a break time");
      return;
    }

    // Verify the slot time is valid for booking
    const bookingTime = toHHMM(slot.start);
    if (!isTimeInFuture(bookingTime) && selectedDate === getCurrentDateYmd()) {
      // Still allow if within 15 minute grace period
      const nowMinutes = getCurrentTimeMinutes();
      const slotMinutes = toMinutes(bookingTime);
      if (slotMinutes !== null && nowMinutes - slotMinutes > 15) {
        toast.error("Cannot assign: This time is too far in the past");
        return;
      }
    }

    // Get services mapped to this employee
    const mappedServices = services.filter((service) => {
      const employeeIds = (service.employees || [])
        .map((e) => e.employeeId)
        .filter(Boolean);
      return employeeIds.includes(employee.id);
    });

    if (!mappedServices.length) {
      toast.error("This employee is not mapped to any service");
      return;
    }

    const defaultServiceId = mappedServices[0]?.serviceId || "";
    setSelectedEmployee(employee);
    setSelectedSlot(slot);
    setManualAssignableServices(mappedServices);
    setManualAssignForm({
      serviceId: defaultServiceId,
      name: "",
      mobile: "",
      email: "",
      paymentMode: "cash",
    });
    setManualAssignOpen(true);
  };

  const closeManualAssignModal = () => {
    if (assignSubmitting) return;
    setManualAssignOpen(false);
    setManualAssignableServices([]);
    setSelectedSlot(null);
    setSelectedEmployee(null);
  };

  const handleManualAssignSubmit = async () => {
    if (!selectedEmployee?.id || !selectedSlot || !selectedDate) {
      toast.error("Missing employee, slot, or date");
      return;
    }

    // Check again if slot is past (double-check)
    const status = getSlotStatus(selectedSlot);
    if (status === "past") {
      toast.error("Cannot assign: This time slot has already passed");
      return;
    }

    if (selectedSlot.type === "break") {
      toast.error("Cannot assign: This is a break time");
      return;
    }

    if (
      !manualAssignForm.serviceId ||
      !manualAssignForm.name ||
      !manualAssignForm.mobile
    ) {
      toast.error("Service, customer name, and mobile are required");
      return;
    }

    const selectedService = manualAssignableServices.find(
      (service) => service.serviceId === manualAssignForm.serviceId,
    );
    if (!selectedService) {
      toast.error("Selected service not found");
      return;
    }

    // Calculate booking time (use slot start as booking time)
    let bookingTime = toHHMM(selectedSlot.start);

    // If the slot start time is in the past but within grace period, use current time
    const nowMinutes = getCurrentTimeMinutes();
    const slotMinutes = toMinutes(bookingTime);
    if (
      slotMinutes !== null &&
      nowMinutes - slotMinutes > 0 &&
      nowMinutes - slotMinutes <= 15
    ) {
      // Use current time rounded to nearest minute
      bookingTime = toHHMM(nowMinutes);
    }

    // Verify the booking time is valid
    if (!isTimeInFuture(bookingTime)) {
      toast.error(
        "Cannot assign: Selected time is in the past. Please choose a future time slot.",
      );
      return;
    }

    // Check if this time overlaps with existing bookings
    const existingBooking = activeBookings.find(
      (b) => b.employeeId === selectedEmployee.id && b.time === bookingTime,
    );

    if (existingBooking) {
      toast.error("This time slot is already booked for this employee");
      return;
    }

    setAssignSubmitting(true);
    try {
      const bookingPayload = {
        serviceId: selectedService.serviceId,
        date: selectedDate,
        time: bookingTime,
        employeeId: selectedEmployee.id,
        employeeName: selectedEmployee.name,
        name: manualAssignForm.name,
        email: manualAssignForm.email,
        mobile: manualAssignForm.mobile,
        amount: getServiceAmount(selectedService),
        paymentMode: manualAssignForm.paymentMode || "cash",
      };

      console.log("Creating booking with payload:", bookingPayload);

      const created = await createBooking(bussinessUuid, bookingPayload);
      const bookingId = created?.booking?._id;
      if (!bookingId) {
        throw new Error("Booking creation failed");
      }

      await updateBookingStatus(bussinessUuid, bookingId, "Confirmed");
      await reloadBookings();
      toast.success("Service assigned successfully!");
      setManualAssignOpen(false);
    } catch (error) {
      console.error("Manual booking assignment failed", error);
      toast.error(error?.response?.data?.message || "Failed to assign service");
    } finally {
      setAssignSubmitting(false);
    }
  };

  const getServiceAmount = (service) => {
    if (!service) return 0;
    const type = normalizePriceType(service?.pricingType, service?.hourlyRate);
    if (type === "hourly") {
      const durationHours =
        parseDurationMinutes(service?.serviceDuration, 60) / 60;
      return Number(
        (Number(service?.hourlyRate || 0) * durationHours).toFixed(2),
      );
    }
    return Number(service?.salePrice || 0);
  };

  const reloadBookings = async () => {
    if (!bussinessUuid) return;

    try {
      const bookingData = await getBookings(bussinessUuid, {
        date: selectedDate,
      });
      const normalizedBookings = normalizeBookings(bookingData);
      const cachedData = readDashboardCache(bussinessUuid);
      const nextCache = {
        employees: cachedData?.employees || employees,
        services: cachedData?.services || services,
        bookingsByDate: {
          ...(cachedData?.bookingsByDate || {}),
          [selectedDate]: normalizedBookings,
        },
      };

      writeDashboardCache(bussinessUuid, nextCache);
      setBookings(normalizedBookings);
    } catch (error) {
      console.error("Failed to refresh booking dashboard data", error);
    }
  };

  const getSlotDisplayTime = (slot) => {
    return `${toHHMM(slot.start)} - ${toHHMM(slot.end)}`;
  };

  // Get the exact booking time that will be assigned
  const getBookingTime = (slot) => {
    // If slot start is in the past but within grace period, use current time
    const nowMinutes = getCurrentTimeMinutes();
    const slotMinutes = slot.start;
    if (
      selectedDate === getCurrentDateYmd() &&
      slotMinutes < nowMinutes &&
      nowMinutes - slotMinutes <= 15
    ) {
      return toHHMM(nowMinutes);
    }
    return toHHMM(slot.start);
  };

  // Check if slot is partially in the past (for displaying remaining time)
  const getSlotRemainingTime = (slot) => {
    if (!selectedDate) return null;

    const todayYmd = getCurrentDateYmd();
    if (selectedDate !== todayYmd) return null;

    const nowMinutes = getCurrentTimeMinutes();
    const slotStart = slot.start;
    const slotEnd = slot.end;

    // If slot hasn't started yet but will start within 15 minutes
    if (slotStart > nowMinutes && slotStart - nowMinutes <= 15) {
      const waitMinutes = slotStart - nowMinutes;
      return `Starts in ${waitMinutes} min`;
    }

    if (nowMinutes >= slotStart && nowMinutes < slotEnd) {
      const remainingMinutes = slotEnd - nowMinutes;
      if (remainingMinutes < 60) {
        return `${remainingMinutes} min remaining`;
      } else {
        const hours = Math.floor(remainingMinutes / 60);
        const mins = remainingMinutes % 60;
        return `${hours}h ${mins}m remaining`;
      }
    }
    return null;
  };

  const renderSlotContent = (slot, employee) => {
    const status = getSlotStatus(slot);
    const displayTime = getSlotDisplayTime(slot);
    const isPast = status === "past" || slot.type === "past";
    const isFuture = status === "future";
    const isCurrent = status === "current";
    const remainingTime = getSlotRemainingTime(slot);
    const bookingTime = getBookingTime(slot);

    // Check if the booking time is valid for assignment
    const isTimeValid =
      isTimeInFuture(bookingTime) || selectedDate !== getCurrentDateYmd();

    // Break slot
    if (slot.type === "break") {
      return (
        <div className='bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-700'>
          <div className='flex items-center gap-2 text-amber-600 dark:text-amber-400'>
            <MdFreeBreakfast size={16} />
            <span className='font-semibold'>Break Time</span>
          </div>
          <p className='text-xs text-amber-600 dark:text-amber-400 mt-1'>
            {displayTime}
          </p>
          <div className='mt-2 text-xs text-amber-500 dark:text-amber-500 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded'>
            🔒 No assignments
          </div>
        </div>
      );
    }

    // Booked slot
    if (slot.type === "booked") {
      const booking = slot.bookings[0];
      const service = slot.service || servicesById.get(booking?.serviceId);
      const priceType = normalizePriceType(
        booking?.servicePriceType || service?.pricingType || "fixed",
        service?.hourlyRate,
      );

      return (
        <div className='bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-700'>
          <div className='flex items-center gap-2 text-green-600 dark:text-green-400'>
            <MdCheckCircle size={16} />
            <span className='font-semibold'>Booked</span>
          </div>
          <p className='text-sm font-medium text-green-700 dark:text-green-300 mt-1'>
            {booking.serviceName || service?.serviceName || "Service"}
          </p>
          <p className='text-xs text-green-600 dark:text-green-400'>
            ⏰ {displayTime}
          </p>
          <p className='text-xs text-green-600 dark:text-green-400'>
            👤 {booking.name}
          </p>
          <p className='text-xs text-green-600 dark:text-green-400'>
            💰 {priceType === "hourly" ? "Hourly" : "Fixed"}: ₹
            {Number(booking.amount || 0).toFixed(2)}
          </p>
        </div>
      );
    }

    // Past slot
    if (isPast) {
      return (
        <div className='bg-gray-100 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-300 dark:border-gray-600 opacity-70'>
          <div className='flex items-center gap-2 text-gray-500 dark:text-gray-400'>
            <MdHistory size={16} />
            <span className='font-semibold'>Past</span>
          </div>
          <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
            {displayTime}
          </p>
          <div className='mt-2 text-xs text-gray-400 dark:text-gray-500 bg-gray-200 dark:bg-gray-700/50 px-2 py-1 rounded'>
            ⏰ Time has passed
          </div>
        </div>
      );
    }

    // Current slot
    if (isCurrent && isTimeValid) {
      return (
        <div className='bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-300 dark:border-blue-700'>
          <div className='flex items-center gap-2 text-blue-600 dark:text-blue-400'>
            <MdRadioButtonUnchecked size={16} />
            <span className='font-semibold'>
              {remainingTime && remainingTime.includes("Starts in")
                ? "Upcoming Slot"
                : "Current Slot"}
            </span>
          </div>
          <p className='text-sm text-blue-600 dark:text-blue-400 mt-1'>
            {displayTime}
          </p>
          {remainingTime && (
            <p className='text-xs text-blue-600 dark:text-blue-400 mt-1'>
              ⏳ {remainingTime}
            </p>
          )}
          <button
            type='button'
            onClick={() => openManualAssignModal(employee, slot)}
            className='mt-2 w-full rounded-md bg-blue-600 dark:bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors'
          >
            📋 Assign Service (starts at {bookingTime})
          </button>
        </div>
      );
    }

    // Future slot
    if (isFuture && isTimeValid) {
      return (
        <div className='bg-white dark:bg-gray-800 p-3 rounded-lg border border-green-200 dark:border-green-700 hover:shadow-lg transition-shadow'>
          <div className='flex items-center gap-2 text-green-600 dark:text-green-400'>
            <MdRadioButtonUnchecked size={16} />
            <span className='font-semibold'>Free Slot</span>
          </div>
          <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
            {displayTime}
          </p>
          <button
            type='button'
            onClick={() => openManualAssignModal(employee, slot)}
            className='mt-2 w-full rounded-md bg-blue-600 dark:bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors'
          >
            📋 Assign Service (starts at {bookingTime})
          </button>
        </div>
      );
    }

    // Fallback for any other case - show as disabled
    return (
      <div className='bg-gray-50 dark:bg-gray-800/30 p-3 rounded-lg border border-gray-200 dark:border-gray-600 opacity-60'>
        <div className='flex items-center gap-2 text-gray-400 dark:text-gray-500'>
          <MdRadioButtonUnchecked size={16} />
          <span className='font-semibold'>Unavailable</span>
        </div>
        <p className='text-sm text-gray-400 dark:text-gray-500 mt-1'>
          {displayTime}
        </p>
      </div>
    );
  };

  return (
    <div className='min-h-screen bg-[#f2f6fc] dark:bg-[#0f1a2e] px-4 py-6 sm:px-6 lg:px-8 transition-colors duration-200'>
      <div className='mx-auto max-w-7xl space-y-6'>
        {/* Header Card */}
        <div className='rounded-2xl bg-white dark:bg-[#1a2744] p-5 shadow-sm border border-[#e6edf5] dark:border-[#2a3f60] sm:p-6 transition-colors duration-200'>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
            <div>
              <h1 className='text-2xl font-bold text-[#0a2a44] dark:text-[#e8f0fe] flex items-center gap-2'>
                <MdCalendarToday
                  className='text-[#1a5fb0] dark:text-[#4a8fd4]'
                  size={24}
                />
                Booking Dashboard
              </h1>
              <p className='mt-1 text-sm text-[#3a5a7a] dark:text-[#8aa9c9]'>
                View and manage employee schedules with time slots
              </p>
              <div className='mt-2 text-sm font-medium text-gray-700 dark:text-gray-300'>
                Current Time:{" "}
                {new Date().toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </div>
            </div>

            <div className='flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto mt-4 sm:mt-0'>
              <div className='flex items-center gap-2 bg-[#f8fcff] dark:bg-[#1a2a44] border border-[#dae6f2] dark:border-[#2a3f60] rounded-xl px-3 py-1.5 w-full sm:w-auto'>
                <MdSearch
                  className='text-[#1a5fb0] dark:text-[#4a8fd4]'
                  size={18}
                />
                <input
                  type='text'
                  placeholder='Search employees...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='bg-transparent border-none outline-none text-sm font-medium text-[#0a2a44] dark:text-[#e8f0fe] py-1 px-1 w-full sm:w-48 placeholder-gray-400 dark:placeholder-gray-500'
                />
              </div>
              <div className='flex items-center gap-2 bg-[#f8fcff] dark:bg-[#1a2a44] border border-[#dae6f2] dark:border-[#2a3f60] rounded-xl px-3 py-1.5 w-full sm:w-auto'>
                <MdCalendarToday
                  className='text-[#1a5fb0] dark:text-[#4a8fd4]'
                  size={16}
                />
                <input
                  type='date'
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  className='bg-transparent border-none outline-none text-sm font-medium text-[#0a2a44] dark:text-[#e8f0fe] py-1 px-1 w-full sm:w-36 dark:[color-scheme:dark]'
                />
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className='mt-5 flex flex-wrap gap-3'>
            <div className='flex items-center gap-1.5 text-xs'>
              <span className='inline-block w-4 h-4 rounded bg-white border border-green-200'></span>
              <span className='text-gray-600 dark:text-gray-400'>
                Free (Future)
              </span>
            </div>
            <div className='flex items-center gap-1.5 text-xs'>
              <span className='inline-block w-4 h-4 rounded bg-blue-50 border border-blue-300'></span>
              <span className='text-gray-600 dark:text-gray-400'>
                Current/Upcoming
              </span>
            </div>
            <div className='flex items-center gap-1.5 text-xs'>
              <span className='inline-block w-4 h-4 rounded bg-gray-100 border border-gray-300'></span>
              <span className='text-gray-600 dark:text-gray-400'>Past</span>
            </div>
            <div className='flex items-center gap-1.5 text-xs'>
              <span className='inline-block w-4 h-4 rounded bg-green-50 border border-green-200'></span>
              <span className='text-gray-600 dark:text-gray-400'>Booked</span>
            </div>
            <div className='flex items-center gap-1.5 text-xs'>
              <span className='inline-block w-4 h-4 rounded bg-amber-50 border border-amber-200'></span>
              <span className='text-gray-600 dark:text-gray-400'>Break</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className='rounded-2xl bg-white dark:bg-[#1a2744] p-4 shadow-sm border border-[#e6edf5] dark:border-[#2a3f60] sm:p-5 transition-colors duration-200'>
          {loading ? (
            <LogoLoading />
          ) : filteredEmployeeSlotsData.length === 0 ? (
            <p className='py-8 text-center text-[#3a5a7a] dark:text-[#8aa9c9]'>
              {searchQuery
                ? "No employees match your search"
                : "No employees found for this date"}
            </p>
          ) : (
            <div className='space-y-6'>
              {filteredEmployeeSlotsData.map((employeeData) => {
                // Split free slots by current time for each employee
                const processedSlots = [];
                employeeData.slots.forEach((slot) => {
                  if (slot.type === "free") {
                    const splitSlots = splitSlotByCurrentTime(slot);
                    processedSlots.push(...splitSlots);
                  } else {
                    processedSlots.push(slot);
                  }
                });

                return (
                  <div
                    key={employeeData.employee.id}
                    className='border border-[#eaf0f7] dark:border-[#2a3f60] rounded-xl overflow-hidden'
                  >
                    {/* Employee Header */}
                    <div className='bg-[#f8fcff] dark:bg-[#0f1a2e] px-4 py-3 border-b border-[#eaf0f7] dark:border-[#2a3f60]'>
                      <div className='flex items-center gap-2'>
                        <MdPerson
                          className='text-[#1a5fb0] dark:text-[#4a8fd4]'
                          size={18}
                        />
                        <span className='font-semibold text-[#0a2a44] dark:text-[#e8f0fe]'>
                          {employeeData.employee.name}
                        </span>
                        <span className='text-xs text-[#3a5a7a] dark:text-[#8aa9c9] ml-2'>
                          ({employeeData.bookings.length} bookings today)
                        </span>
                      </div>
                    </div>

                    {/* Time Slots Grid */}
                    <div className='p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'>
                      {processedSlots.map((slot, index) => (
                        <div key={index}>
                          {renderSlotContent(slot, employeeData.employee)}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Manual Assign Modal */}
        {manualAssignOpen && selectedEmployee && selectedSlot && (
          <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
            <div className='w-full max-w-lg rounded-2xl bg-white dark:bg-[#1a2744] p-5 shadow-xl border border-[#e2ebf4] dark:border-[#2a3f60]'>
              <div className='mb-4 flex items-center justify-between'>
                <h3 className='text-lg font-bold text-[#0a2a44] dark:text-[#e8f0fe]'>
                  Assign Service to {selectedEmployee.name}
                </h3>
                <button
                  type='button'
                  onClick={closeManualAssignModal}
                  className='rounded-md p-1 text-[#376089] dark:text-[#8aa9c9] hover:bg-[#eef4fb] dark:hover:bg-[#0f1a2e]'
                >
                  <MdClose size={18} />
                </button>
              </div>

              <div className='space-y-3 text-sm'>
                <div className='grid grid-cols-2 gap-2'>
                  <div className='rounded-lg bg-[#f7fbff] dark:bg-[#0f1a2e] border border-[#e2ebf5] dark:border-[#2a3f60] p-2'>
                    <p className='text-[11px] uppercase text-[#4f7497] dark:text-[#8aa9c9]'>
                      Time Slot
                    </p>
                    <p className='font-semibold text-[#0a2a44] dark:text-[#e8f0fe]'>
                      {getSlotDisplayTime(selectedSlot)}
                    </p>
                  </div>
                  <div className='rounded-lg bg-[#f7fbff] dark:bg-[#0f1a2e] border border-[#e2ebf5] dark:border-[#2a3f60] p-2'>
                    <p className='text-[11px] uppercase text-[#4f7497] dark:text-[#8aa9c9]'>
                      Booking Start
                    </p>
                    <p className='font-semibold text-blue-600 dark:text-blue-400'>
                      ⏰ {getBookingTime(selectedSlot)}
                    </p>
                  </div>
                </div>

                <div className='rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-2'>
                  <p className='text-[11px] text-blue-600 dark:text-blue-400'>
                    📌 Service will start at{" "}
                    <strong>{getBookingTime(selectedSlot)}</strong> and continue
                    for the service duration
                  </p>
                </div>

                <div>
                  <label className='mb-1 block text-xs font-semibold uppercase tracking-wide text-[#3f6385] dark:text-[#8aa9c9]'>
                    Select Service
                  </label>
                  <select
                    value={manualAssignForm.serviceId}
                    onChange={(event) =>
                      setManualAssignForm((prev) => ({
                        ...prev,
                        serviceId: event.target.value,
                      }))
                    }
                    className='w-full rounded-lg border border-[#d4e1ef] dark:border-[#2a3f60] bg-white dark:bg-[#0f1a2e] text-[#0a2a44] dark:text-[#e8f0fe] px-3 py-2 outline-none focus:border-[#1a5fb0] dark:focus:border-[#4a8fd4]'
                  >
                    <option value=''>Select Service</option>
                    {manualAssignableServices.map((service) => {
                      const duration = parseDurationMinutes(
                        service.serviceDuration,
                        60,
                      );
                      return (
                        <option
                          key={service.serviceId}
                          value={service.serviceId}
                        >
                          {service.serviceName} ({duration} min,{" "}
                          {normalizePriceType(
                            service.pricingType,
                            service.hourlyRate,
                          ) === "hourly"
                            ? "Hourly"
                            : "Fixed"}
                          )
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  <div>
                    <label className='mb-1 block text-xs font-semibold uppercase tracking-wide text-[#3f6385] dark:text-[#8aa9c9]'>
                      Customer Name
                    </label>
                    <input
                      type='text'
                      value={manualAssignForm.name}
                      onChange={(event) =>
                        setManualAssignForm((prev) => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                      className='w-full rounded-lg border border-[#d4e1ef] dark:border-[#2a3f60] bg-white dark:bg-[#0f1a2e] text-[#0a2a44] dark:text-[#e8f0fe] px-3 py-2 outline-none focus:border-[#1a5fb0] dark:focus:border-[#4a8fd4]'
                      placeholder='Enter customer name'
                    />
                  </div>
                  <div>
                    <label className='mb-1 block text-xs font-semibold uppercase tracking-wide text-[#3f6385] dark:text-[#8aa9c9]'>
                      Mobile
                    </label>
                    <input
                      type='text'
                      value={manualAssignForm.mobile}
                      onChange={(event) =>
                        setManualAssignForm((prev) => ({
                          ...prev,
                          mobile: event.target.value,
                        }))
                      }
                      className='w-full rounded-lg border border-[#d4e1ef] dark:border-[#2a3f60] bg-white dark:bg-[#0f1a2e] text-[#0a2a44] dark:text-[#e8f0fe] px-3 py-2 outline-none focus:border-[#1a5fb0] dark:focus:border-[#4a8fd4]'
                      placeholder='Enter mobile number'
                    />
                  </div>
                </div>

                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  <div>
                    <label className='mb-1 block text-xs font-semibold uppercase tracking-wide text-[#3f6385] dark:text-[#8aa9c9]'>
                      Email
                    </label>
                    <input
                      type='email'
                      value={manualAssignForm.email}
                      onChange={(event) =>
                        setManualAssignForm((prev) => ({
                          ...prev,
                          email: event.target.value,
                        }))
                      }
                      className='w-full rounded-lg border border-[#d4e1ef] dark:border-[#2a3f60] bg-white dark:bg-[#0f1a2e] text-[#0a2a44] dark:text-[#e8f0fe] px-3 py-2 outline-none focus:border-[#1a5fb0] dark:focus:border-[#4a8fd4]'
                      placeholder='Enter email'
                    />
                  </div>
                  <div>
                    <label className='mb-1 block text-xs font-semibold uppercase tracking-wide text-[#3f6385] dark:text-[#8aa9c9]'>
                      Payment Mode
                    </label>
                    <select
                      value={manualAssignForm.paymentMode}
                      onChange={(event) =>
                        setManualAssignForm((prev) => ({
                          ...prev,
                          paymentMode: event.target.value,
                        }))
                      }
                      className='w-full rounded-lg border border-[#d4e1ef] dark:border-[#2a3f60] bg-white dark:bg-[#0f1a2e] text-[#0a2a44] dark:text-[#e8f0fe] px-3 py-2 outline-none focus:border-[#1a5fb0] dark:focus:border-[#4a8fd4]'
                    >
                      <option value='cash'>Cash</option>
                      <option value='upi'>UPI</option>
                      <option value='card'>Card</option>
                      <option value='online'>Online</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className='mt-5 flex justify-end gap-2'>
                <button
                  type='button'
                  onClick={closeManualAssignModal}
                  className='rounded-lg border border-[#cfdceb] dark:border-[#2a3f60] px-4 py-2 text-sm font-semibold text-[#2f5d88] dark:text-[#8aa9c9] hover:bg-[#f2f7fd] dark:hover:bg-[#0f1a2e]'
                  disabled={assignSubmitting}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={handleManualAssignSubmit}
                  className='rounded-lg bg-[#1a5fb0] dark:bg-[#2a6fc0] px-4 py-2 text-sm font-semibold text-white hover:bg-[#164f92] dark:hover:bg-[#1a5fb0] disabled:opacity-60'
                  disabled={assignSubmitting}
                >
                  {assignSubmitting ? "Assigning..." : "Assign Service"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
